import { roundToString } from '@lib/calculations/util';
import { UserWithSettingsResponse } from '@lib/responses';
import { convertFromDisplayMagnitude } from '@lib/util/display-magnitude';
import { DisplayMagnitude } from '@prisma/client';
import { Decimal } from '@snap-types/types/decimal';
import { commaSeparated } from '@ui/util/numbers';

import { PDFProviderType } from '../../components/PDF/PDFProvider';
import { siteQuoteDocumentSchema } from '../../components/PDF/SiteQuoteDocuments';
import { QuoteBuilderStateType } from '../../types/state';

function formatQuantity(
  quantityDisplayMagnitude: DisplayMagnitude,
  quantity: Decimal,
) {
  return commaSeparated(
    roundToString(
      convertFromDisplayMagnitude(
        quantity.toNumber(),
        quantityDisplayMagnitude,
      ),
      { min: 0, small: 0 },
    ),
  );
}

// @TODO Validate here;
export function mergeFormAndState(
  state: QuoteBuilderStateType,
  user: UserWithSettingsResponse,
  quantityDisplayMagnitude: DisplayMagnitude,
  formattedQuoteTotal: string | null,
): PDFProviderType {
  const pricingUnit =
    quantityDisplayMagnitude === DisplayMagnitude.ONES
      ? 'Price Each'
      : 'Price/M';

  const decimalPlaces = state.calculationConfig.decimalPlaces;

  return {
    customer: {
      id: state.customerId,
      attentionName: state.contactName,
      name: state.name,
      address1: state.address1,
      address2: state.address2,
      city: state.city,
      state: state.state,
      postalCode: state.postalCode,
      countryCode: state.countryCode,
      email: state.contactEmail,
      phone: state.contactPhone,
      termsNetDays: state.termsNetDays,
      salesRepId: state.salesRepId,
      salesRepName: state.salesRepName,
      salesRepEmail: state.salesRepEmail,
      salesRepPhone: state.salesRepPhone,
      customerQuoteId: state.customerQuoteId,
    },
    daysUntilExpiration: state.daysUntilQuoteExpires,
    specs: state.specs,
    siteCode: siteQuoteDocumentSchema.parse(state.siteCode.toLowerCase()),
    quoteId: state.quoteId,
    leadTimeFormat: state.leadTimeFormat,
    formattedQuoteTotal,
    quoteLines: state.quoteLineUuids.map((id, index) => {
      const quoteLine = state.quoteLines[id];

      return {
        estQtyOnHand: quoteLine.estQtyOnHand,
        id,
        leadTime: quoteLine.leadTime,
        leadTimeNotes: quoteLine.leadTimeNotes,
        lineNumber: index + 1,
        specs: quoteLine.specs,
        snapQuoteLineSource: quoteLine.snapQuoteLineSource,
        part: {
          partId: quoteLine.finishedPartId,
          customerPartId: quoteLine.customerPartId,
          originalSource: quoteLine.partOriginalSource,
          description: quoteLine.description,
          drawingId: quoteLine.snapWorkOrder?.drawingId,
          drawingRevision: quoteLine.snapWorkOrder?.drawingRevisionNo,
          partSpecs: quoteLine.partSpecs,
        },
        partOptions:
          quoteLine.productOptionsUuids?.map((uuid) => {
            const option = quoteLine.productOptions[uuid];

            return {
              id: uuid,
              name: option.optionName,
              groupName: option.groupName,
            };
          }) ?? [],
        quotePrices: quoteLine.quotePriceUuids.map((id) => {
          const quotePrice = quoteLine.quotePrices[id];

          return {
            id,
            quantity: formatQuantity(
              quantityDisplayMagnitude,
              quotePrice.quantity,
            ),
            unitPrice: `$${commaSeparated(
              quotePrice.unitPrice.toFixed(decimalPlaces),
              { minimumFractionDigits: decimalPlaces },
            )}`,

            total: `$${commaSeparated(
              quotePrice.totalPrice.toFixed(decimalPlaces),
              { minimumFractionDigits: decimalPlaces },
            )}`,
          };
        }),
      };
    }),
    user: {
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
    },
    csrName: state.csrName,
    csrEmail: state.csrEmail,
    pricingUnit,
  };
}
